import React, { MouseEvent } from 'react';
import { LinkProps as LinkElementProps } from 'react-router-dom';
import styled from '@emotion/styled';
import { BsLink45Deg } from '@react-icons/all-files/bs/BsLink45Deg';
import { useContextSelector } from 'use-context-selector';
import { BucketLogData } from '@bucketplace/bucket-log';
import { useIsInApp, useSnackbar } from '@bucketplace/design-system';

import { EditorPlaceholder } from '../../components/EditorPlaceholder';
import { EditorContext, initialLinkContextValue, useLinkContext } from '../../contexts';

// TODO: type으로 only app link를 받아 처리하자
const ONLY_APP_LINK = ['https://ohou.se/kakao_sync/non-kakao-user'];

export type OpenOption = 'sameTab' | 'newTab' | 'anchor';

export interface LinkProps {
  children?: React.ReactNode;
  to: string;
  openOption: OpenOption;
  className?: string;
  style?: React.CSSProperties;
  logData?: BucketLogData;
}

export function Link({ children, ...props }: LinkProps) {
  const { editorType } = useContextSelector(
    EditorContext,
    (context) => context!.state,
  );
  const LinkElement = editorType === 'editor' ? EditorLink : NormalLink;

  return (
    <LinkElement {...props}>
      { children || (
        <EditorPlaceholder
          icon={<BsLink45Deg />}
          message="아이템을 추가해주세요."
        />
      ) }
    </LinkElement>
  );
}

function EditorLink({ children, ...props }: LinkProps) {
  const { to, openOption, className, style } = props;
  const { displayMode } = useContextSelector(
    EditorContext,
    (context) => context!.state,
  );
  const isInApp = useIsInApp(location);
  const { open } = useSnackbar();
  const { callbacks } = useLinkContext();
  const { callbacks: initialCallback } = initialLinkContextValue;
  const onClick = callbacks?.onClick ?? initialCallback.onClick;

  function handleClick(e: MouseEvent) {
    e.preventDefault();

    if (displayMode === 'edit') { return; }

    if (ONLY_APP_LINK.includes(to) && !isInApp) {
      open({ content: '오늘의집 앱에서 진행해주시기 바랍니다.' });
      return;
    }

    if (openOption === 'anchor') {
      onClick?.(to);
      return;
    }
    window.open(to, '_blank')?.focus();
  }

  return (
    <EditorLinkButton
      type="button"
      onClick={handleClick}
      className={className}
      style={style}
    >
      { children }
    </EditorLinkButton>
  );
}

const EditorLinkButton = styled.a`
  cursor: pointer;
`;

function NormalLink({ children, ...props }: LinkProps) {
  const { to, openOption, className, style, logData } = props;
  const { element, callbacks } = useLinkContext();
  const isInApp = useIsInApp(location);
  const { open } = useSnackbar();
  const { element: InitialElement, callbacks: initialCallback } = initialLinkContextValue;
  const LinkElement = element ?? InitialElement;
  const onClick = callbacks?.onClick ?? initialCallback.onClick;

  const getLinkProperties = (): LinkElementProps => {
    if (ONLY_APP_LINK.includes(to) && !isInApp) {
      return {
        to,
        onClick: (e) => {
          e.preventDefault();

          open({ content: '오늘의집 앱에서 진행해주시기 바랍니다.' });
        },
      };
    }

    switch (openOption) {
      case 'newTab':
        return {
          to,
          target: '_blank',
          rel: 'noopener noreferrer',
        };
      case 'anchor':
        return {
          to: `#${to}`,
          onClick: () => onClick?.(to),
        };
      case 'sameTab':
      default:
        return {
          to,
        };
    }
  };

  const linkProperties = getLinkProperties();

  return (
    <LinkElement
      className={className}
      style={{
        ...style,
        textDecoration: 'none',
        cursor: 'pointer',
      }}
      logData={logData}
      openOption={openOption}
      {...linkProperties}
    >
      { children }
    </LinkElement>
  );
}
